/**
 * Rates — tax, dual pricing, discounts, margins.
 *
 * Stored as integer **parts per million** rather than floats. 8.375% sales tax
 * is not representable in binary floating point; as 83_750 ppm it is exact, and
 * it round-trips through JSON, Postgres, and the wire without drift.
 *
 *   1%      =    10_000 ppm
 *   3.5%    =    35_000 ppm
 *   8.375%  =    83_750 ppm
 *   100%    = 1_000_000 ppm
 */

declare const RatePpmBrand: unique symbol;

/** A rate in parts per million. Always an integer. Construct via {@link ratePpm} or {@link percentToPpm}. */
export type RatePpm = number & { readonly [RatePpmBrand]: true };

/** 1_000_000 ppm === 100% === 1.0 */
export const PPM_SCALE = 1_000_000;

export const ZERO_RATE = 0 as RatePpm;

/** Wrap a raw ppm integer. Throws if it is not a finite integer. */
export function ratePpm(value: number): RatePpm {
  if (!Number.isInteger(value)) {
    throw new TypeError(`ratePpm: expected an integer ppm value, received ${value}`);
  }
  if (!Number.isSafeInteger(value)) {
    throw new RangeError(`ratePpm: value out of safe integer range: ${value}`);
  }
  return value as RatePpm;
}

/**
 * Convert a human percentage to ppm. `percentToPpm(8.375) === 83_750`.
 *
 * Rounds to the nearest ppm, so at most 4 decimal places of a percent survive —
 * finer than any real tax jurisdiction or card program.
 */
export function percentToPpm(percent: number): RatePpm {
  if (!Number.isFinite(percent)) {
    throw new TypeError(`percentToPpm: expected a finite number, received ${percent}`);
  }
  return ratePpm(Math.round(percent * (PPM_SCALE / 100)));
}

/** Convert ppm back to a human percentage. `ppmToPercent(83_750) === 8.375`. */
export function ppmToPercent(rate: RatePpm): number {
  return rate / (PPM_SCALE / 100);
}

/**
 * Render a rate for display: `formatPercent(83_750) === '8.375%'`.
 * Trailing zeros are trimmed so 5.5% never shows as "5.5000%".
 */
export function formatPercent(rate: RatePpm, maxFractionDigits = 4): string {
  const percent = ppmToPercent(rate);
  const fixed = percent.toFixed(maxFractionDigits);
  const trimmed = fixed.includes('.') ? fixed.replace(/\.?0+$/, '') : fixed;
  return `${trimmed}%`;
}

export function addRates(a: RatePpm, b: RatePpm): RatePpm {
  return ratePpm(a + b);
}
